import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@soundbyte/config';
import { Button } from './button';

/**
 * Modal — built on Radix Dialog so it gets portal rendering, focus trap,
 * focus restoration, `aria-modal="true"`, reference-counted body-scroll lock
 * (no more stacked-modal scroll bugs), and ESC/outside-click behaviour for
 * free. Framer Motion animates over the Radix overlay/content.
 */

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-4xl',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
}) => {
  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AnimatePresence>
        {isOpen && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content
              asChild
              // If the dialog has no visible title, Radix still requires one
              // for screen readers — we surface it as a hidden VisuallyHidden
              // fallback below. onOpenAutoFocus/onCloseAutoFocus left default
              // so the focus trap behaves normally.
              aria-describedby={description ? 'modal-description' : undefined}
            >
              {/* The Content element is an unanimated flex wrapper that
                  handles centering. The animated card lives inside it.
                  Reason: framer-motion's `transform` (scale/y) would override
                  Tailwind's `-translate-x/y-1/2`, breaking centering. By
                  splitting the concerns, transforms only affect the entrance
                  animation, never the layout. */}
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <motion.div
                  key="content"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className={cn(
                    'relative w-full pointer-events-auto',
                    'rounded-xl border border-border bg-card p-6 shadow-xl',
                    // Cap to viewport height and allow internal scroll so
                    // tall content doesn't push the modal off-screen.
                    'max-h-[90vh] overflow-y-auto',
                    sizeClasses[size]
                  )}
                >
                {/* Title — always required by Radix for a11y. If the caller
                    didn't provide one, render a visually-hidden fallback so
                    screen readers still announce the dialog. */}
                {title ? (
                  <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
                    {title}
                  </DialogPrimitive.Title>
                ) : (
                  <DialogPrimitive.Title className="sr-only">Dialog</DialogPrimitive.Title>
                )}
                {description && (
                  <DialogPrimitive.Description id="modal-description" className="mt-1 text-sm text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                )}

                {showCloseButton && (
                  <DialogPrimitive.Close asChild>
                    <button
                      type="button"
                      aria-label="Close dialog"
                      className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </DialogPrimitive.Close>
                )}

                {/* Vertical spacing between header and content matches the
                    previous design — only add the mb-4 spacer when there's
                    a visible header to avoid an empty gap on untitled dialogs. */}
                <div className={cn((title || description) && 'mt-4')}>
                  {children}
                </div>
                </motion.div>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
};

// Confirm Dialog variant
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  isLoading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  isLoading = false,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <p className="text-foreground/90 mb-6">{message}</p>
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          {cancelText}
        </Button>
        <Button
          variant={variant === 'destructive' ? 'destructive' : 'default'}
          onClick={onConfirm}
          isLoading={isLoading}
        >
          {confirmText}
        </Button>
      </div>
    </Modal>
  );
};

export { Modal, ConfirmModal };
